import Box from "@mui/material/Box";
import moment from "moment";
import PropTypes from "prop-types";

import {
  buildVelocityWindowSummary,
  isInPanelPeriod,
} from "./VelocityWindowSummary";

// Behind the original target by more than this (in %) counts as off pace.
const OFF_PACE_PERCENT = 10;

const VELOCITY_FILTERS = [
  { id: "all", label: "All ASINs" },
  { id: "peak", label: "In peak window" },
  { id: "offPace", label: `Off pace >${OFF_PACE_PERCENT}%` },
];

// The velocity tool entries of a product: its own ASIN, or all its children.
const predictedUnitsForProduct = (product, predictedUnitsList) => {
  const asins = product.children
    ? product.children.map((c) => c.child_asin)
    : [product.child_asin];
  return Object.fromEntries(
    asins
      .filter((asin) => predictedUnitsList?.[asin])
      .map((asin) => [asin, predictedUnitsList[asin]])
  );
};

// inPeakWindow: the product's window summary panel is showing, i.e. from
// VELOCITY_WINDOW_PANEL_MARGIN_DAYS before the window starts to as many days
// after it ends. offPace: the window is running and the product is behind its
// original target by more than OFF_PACE_PERCENT.
const getVelocityStatus = (
  product,
  predictedUnitsList,
  today = moment().format("YYYY-MM-DD")
) => {
  const summary = buildVelocityWindowSummary(
    predictedUnitsForProduct(product, predictedUnitsList),
    today
  );
  if (!summary) return { inPeakWindow: false, offPace: false };

  const isRunning = summary.start <= today && summary.end >= today;
  const { comparison } = summary;

  return {
    inPeakWindow: isInPanelPeriod(summary, today),
    offPace:
      isRunning &&
      !!comparison &&
      comparison.difference < 0 &&
      comparison.percent !== null &&
      comparison.percent > OFF_PACE_PERCENT,
  };
};

const matchesVelocityFilter = (filterId, status) =>
  filterId === "peak"
    ? status.inPeakWindow
    : filterId === "offPace"
    ? status.offPace
    : true;

const VelocityFilterChips = ({ selected, counts, onSelect, disabled }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
    {VELOCITY_FILTERS.map((filter) => {
      const isSelected = selected === filter.id;
      const isOffPace = filter.id === "offPace";
      return (
        <Box
          key={filter.id}
          component="button"
          type="button"
          disabled={disabled}
          onClick={() => onSelect(filter.id)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
            padding: "6px 14px",
            borderRadius: "999px",
            border: "1px solid",
            borderColor: isSelected ? "#0f172a" : "#cbd5e1",
            backgroundColor: isSelected ? "#0f172a" : "white",
            color: isSelected ? "white" : "#0f172a",
            fontFamily: "Inter",
            fontSize: 13,
            fontWeight: 500,
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.6 : 1,
            "&:hover": disabled
              ? {}
              : { borderColor: isSelected ? "#0f172a" : "#94a3b8" },
          }}
        >
          {filter.label}
          <Box
            component="span"
            sx={{
              padding: "1px 7px",
              borderRadius: "4px",
              fontSize: 11,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              backgroundColor: isSelected
                ? "rgba(255, 255, 255, 0.18)"
                : isOffPace
                ? "#fee2e2"
                : "#f1f5f9",
              color: isSelected ? "white" : isOffPace ? "#b91c1c" : "#475569",
            }}
          >
            {counts[filter.id] ?? 0}
          </Box>
        </Box>
      );
    })}
  </Box>
);

VelocityFilterChips.propTypes = {
  selected: PropTypes.string.isRequired,
  counts: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export { getVelocityStatus, matchesVelocityFilter };
export default VelocityFilterChips;
